import { useEffect, useRef } from "react";
import type { EngineEvent } from "../../shared/types.ts";
import { getEvents } from "./api.ts";
import type { ToastLink } from "../ui/Toaster.tsx";
import type { Tone } from "./ctx.ts";

const EVERY = 10_000;
const TOLD_KEY = "headcount.needs-you.told";

/** The hash that opens the Needs you sheet, which App listens for. */
export const NEEDS_HASH = "#needs-sheet";

/** Opens the Needs you sheet over the page the user is on. */
export const needsYouHref = (path: string) => `${path}${NEEDS_HASH}`;

/** The toast an engine event earns, or null for the quiet ones (queued, applying). `path` is the page the user is on. */
export function eventToast(e: EngineEvent, path = "/applications"): { text: string; tone: Tone; link?: ToastLink } | null {
  const who = e.company ?? "The company";
  const why = (e.reasonLabel || "").replace(/\.$/, "");
  if (e.kind === "failed" && e.needs === "answers") return { text: `${who} needs an answer from you.`, tone: "plain", link: { href: needsYouHref(path), label: "Answer" } };
  if (e.kind === "failed" && e.needs === "site") return { text: `${who} needs you to finish on their site.`, tone: "plain", link: { href: needsYouHref(path), label: "View" } };
  if (e.kind === "submitted") return { text: `Sent to ${who}.`, tone: "ok" };
  if (e.kind === "confirmed") return { text: `${who} confirmed your application.`, tone: "ok" };
  if (e.kind === "failed") return { text: `${who}: ${why || "it didn't go through"}.`, tone: "bad" };
  if (e.kind === "skipped") return { text: `Skipped ${who}${why ? `: ${why}` : ""}.`, tone: "plain" };
  return null;
}

/**
 * Whether this tab already told the user about an application that needs them, marking it told when not. Kept in
 * sessionStorage, so a reload doesn't tell them again.
 */
export function toldBefore(jobId: string, told: Set<string>): boolean {
  try {
    for (const id of JSON.parse(sessionStorage.getItem(TOLD_KEY) ?? "[]") as string[]) told.add(id);
  } catch {}
  if (told.has(jobId)) return true;
  told.add(jobId);
  try {
    sessionStorage.setItem(TOLD_KEY, JSON.stringify([...told].slice(-200)));
  } catch {}
  return false;
}

/** The newest event time in a page, which the API returns newest first. */
export const newest = (events: EngineEvent[]): string | null => events.reduce<string | null>((m, e) => (!m || e.at > m ? e.at : m), null);

/**
 * Polls `/api/events` every 10 seconds while the tab is visible: a toast for each application sent, confirmed or
 * failed, an `engine-event` on window for screens that list applications, and fresh stats when something finished. An
 * application that needs the user is toasted once a session, with a link that opens the Needs you sheet.
 */
export function useEngineEvents(toast: (text: string, tone: Tone, link?: ToastLink) => void, refreshStats: () => void): void {
  const handlers = useRef({ toast, refreshStats });
  handlers.current = { toast, refreshStats };

  useEffect(() => {
    const told = new Set<string>();
    let since: string | null = null;
    let alive = true;
    let timer: ReturnType<typeof setInterval> | null = null;
    const tick = async () => {
      try {
        const r = await getEvents(since);
        if (!alive) return;
        if (since) {
          for (const e of [...r.events].reverse()) {
            const t = eventToast(e, location.pathname + location.search);
            if (t && !(t.link && toldBefore(e.jobId, told))) handlers.current.toast(t.text, t.tone, t.link);
            dispatchEvent(new CustomEvent("engine-event", { detail: e }));
          }
          if (r.events.some((e) => e.kind !== "applying" && e.kind !== "queued")) handlers.current.refreshStats();
        }
        since = newest(r.events) ?? since ?? r.now;
      } catch {}
    };
    const start = () => {
      if (timer || document.visibilityState !== "visible") return;
      tick();
      timer = setInterval(tick, EVERY);
    };
    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };
    const vis = () => (document.visibilityState === "visible" ? start() : stop());
    start();
    document.addEventListener("visibilitychange", vis);
    return () => {
      alive = false;
      stop();
      document.removeEventListener("visibilitychange", vis);
    };
  }, []);
}
